import { useCallback, useMemo } from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl';

import type {
	RefineDropdownChildItem,
	RefineDropdownItem,
} from '@atlassian/generative-ai-modal/screens/Preview';
import type { Suggestion } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';

import type { WritingStyleMenuConfiguration } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';
import {
	getWritingStyleMenuActionItemsForSurface,
	getWritingStyleMenuLabelForSurface,
	getWritingStyleMenuParentConfigItem,
	getWritingStyleMenuTestId,
} from '../prebuilt/writing-style-menu';

const getMessageKey = (message: MessageDescriptor): string => message.id ?? JSON.stringify(message);

export const useWritingStyleMenuRefineItems = ({
	endExperience,
	items,
	onLocalActionClick,
	refineItemByConfigKey,
	writingStyleMenu,
}: {
	endExperience: () => void;
	items: RefineDropdownItem[];
	onLocalActionClick: (itemId: string) => void;
	refineItemByConfigKey: Map<string, RefineDropdownChildItem>;
	writingStyleMenu?: WritingStyleMenuConfiguration;
}): RefineDropdownItem[] =>
	useMemo(() => {
		if (!writingStyleMenu) {
			return items;
		}

		const parentKey = getMessageKey(
			getWritingStyleMenuLabelForSurface(writingStyleMenu, 'command'),
		);
		const menuItems = getWritingStyleMenuActionItemsForSurface(writingStyleMenu, 'command');
		const children = menuItems.flatMap<RefineDropdownChildItem>(
			({ hasSeparator, item: menuItem }) => {
				if (menuItem.type === 'rewrite') {
					const refineItem = refineItemByConfigKey.get(String(menuItem.configItem.config.key));
					return refineItem ? [{ ...refineItem, hasSeparator }] : [];
				}

				return [
					{
						title: menuItem.label,
						hasSeparator,
						onClick: () => {
							onLocalActionClick(menuItem.id);
							endExperience();
							menuItem.onClick();
						},
					},
				];
			},
		);

		if (children.length === 0) {
			return items;
		}

		let hasMatchingParent = false;

		const mappedItems = items.map((item) => {
			if (!('children' in item) || !item.children || getMessageKey(item.title) !== parentKey) {
				return item;
			}
			hasMatchingParent = true;

			return {
				...item,
				children,
			};
		});

		if (hasMatchingParent) {
			return mappedItems;
		}

		return [
			...mappedItems,
			{
				title: getWritingStyleMenuLabelForSurface(writingStyleMenu, 'command'),
				icon: null,
				children,
			},
		];
	}, [endExperience, items, onLocalActionClick, refineItemByConfigKey, writingStyleMenu]);

export const useWritingStyleMenuSuggestions = ({
	endExperience,
	formatMessage,
	onLocalActionClick,
	onMenuParentSelect,
	writingStyleMenu,
}: {
	endExperience: () => void;
	formatMessage: IntlShape['formatMessage'];
	onLocalActionClick: (itemId: string) => void;
	onMenuParentSelect: (configItem: EditorPluginAIConfigItem) => void;
	writingStyleMenu?: WritingStyleMenuConfiguration;
}): ((suggestions: Suggestion[]) => Suggestion[]) =>
	useCallback(
		(suggestions) => {
			if (!writingStyleMenu) {
				return suggestions;
			}

			const menuItems = getWritingStyleMenuActionItemsForSurface(writingStyleMenu, 'command');
			const rewriteKeys = new Set(
				menuItems.flatMap(({ item }) =>
					item.type === 'rewrite' ? [String(item.configItem.config.key)] : [],
				),
			);
			const rewriteSuggestions = new Map(
				suggestions
					.filter((suggestion) => rewriteKeys.has(String(suggestion.value)))
					.map((suggestion) => [String(suggestion.value), suggestion]),
			);
			const parentConfigItem = getWritingStyleMenuParentConfigItem(writingStyleMenu, 'command');
			const rewriteParentSelect = Array.from(rewriteSuggestions.values()).find(
				(suggestion) => suggestion.onParentSelect,
			)?.onParentSelect;

			const orderedMenuSuggestions = menuItems.flatMap<Suggestion>(({ hasSeparator, item }) => {
				if (item.type === 'rewrite') {
					const suggestion = rewriteSuggestions.get(String(item.configItem.config.key));
					return suggestion ? [{ ...suggestion, hasSeparator }] : [];
				}

				return [
					{
						value: `writing-style-local-action:${item.id}`,
						label: formatMessage(item.label),
						icon: item.icon?.({ formatMessage }),
						hasSeparator,
						nestingConfig: parentConfigItem
							? {
									parentTitle: formatMessage(
										getWritingStyleMenuLabelForSurface(writingStyleMenu, 'command'),
									),
									parentTestId: getWritingStyleMenuTestId(writingStyleMenu),
								}
							: undefined,
						onParentSelect:
							rewriteParentSelect ??
							(parentConfigItem ? () => onMenuParentSelect(parentConfigItem) : undefined),
						onSelect: () => {
							onLocalActionClick(item.id);
							endExperience();
							item.onClick();
						},
						// Local actions do not represent prompts, so focus must not update prompt highlighting.
						onSuggestionFocused: () => {},
					},
				];
			});
			const firstRewriteIndex = suggestions.findIndex((suggestion) =>
				rewriteKeys.has(String(suggestion.value)),
			);
			const result = suggestions.filter((suggestion) => !rewriteKeys.has(String(suggestion.value)));
			result.splice(
				firstRewriteIndex === -1 ? result.length : firstRewriteIndex,
				0,
				...orderedMenuSuggestions,
			);
			return result;
		},
		[endExperience, formatMessage, onLocalActionClick, onMenuParentSelect, writingStyleMenu],
	);
